/** Match/dashboard endpoints (architecture §6), over the backend's `matches` router. */

import { httpClient, withMutationTimeout } from './client'
import type {
  ApplicationStatus,
  CheckAnswer,
  Interest,
  Match,
  MatcherV2Decisions,
  MatchesResponse,
  MissingFactAnswer,
  MissingFactAnswerOut,
  ShelfAnswer,
  ShelfAnswerOut,
} from '../types/domain'

/**
 * One matcher-v2 cold-start fit-assessment attempt (backend
 * `routes/matches.py::MatcherV2FitOutcomeOut`, PIVOT-301, R10-A1/A3).
 *
 * `run_id` is the durable run this attempt used or resumed — a repeated
 * `coldStart()` call for the same user returns the SAME `run_id`.
 * `state: 'failed'` means the step itself could not start (never a per-job
 * failure, which the server already absorbs) — distinct from a `null`
 * `matcher_v2_fit` on `ColdStartResponse`, which means the step never
 * applied at all (a legacy-scorer user).
 */
export interface MatcherV2FitOutcome {
  run_id: string | null
  state: 'completed' | 'failed'
  completed: number
  invalid_response: number
}

/** What one cold-start run did — `POST /api/matches/cold-start` (PIVOT-081). */
export interface ColdStartResponse {
  scored: number
  /** Candidates left for the next daily run: the wait is bounded, and what it
   * did not reach is reported rather than dropped silently. */
  deferred: number
  /**
   * Whether this run's lateral picks were read by the exclusion judge before
   * landing on the slate (PIVOT-107) — `false` today, for every run, because
   * no production path builds one yet (`PIVOT-118` owns turning it on,
   * product-wide, once its own cost/quality gates clear). Carried rather than
   * assumed: the day `PIVOT-118` ships, this flips to `true` without this
   * type needing to change again.
   */
  lateral_judged: boolean
  /** `null`/absent for a legacy-scorer user (the matcher-v2 fit step never
   * applies) — see `MatcherV2FitOutcome`'s own docstring. Optional, mirroring
   * the backend's own `= None` default: no UI reads this field yet; mirrored
   * the same commit as the backend response so the two shapes cannot drift
   * (frontend/CLAUDE.md §5). */
  matcher_v2_fit?: MatcherV2FitOutcome | null
}

/**
 * Scores a brand-new user's first shortlist, now (spec §4/§8: instant matches).
 *
 * Called once, at the end of onboarding, after the reviewed profile is saved.
 * Takes seconds — it runs live judge calls rather than the Batch API — so it
 * belongs behind a progress screen, never a silent await.
 *
 * Takes a `signal` like every other call here (PIVOT-133), so the page that
 * started it can cancel it on unmount (frontend/CLAUDE.md §7). It is the one
 * write in this module with **no `withMutationTimeout`**, on purpose: that cap
 * is for the short, user-initiated writes below, and this call runs a live
 * judge over 25 offers. `OnboardingProgress` shows a patience line at
 * `PATIENCE_MS` (10s) precisely because this wait routinely passes
 * `MUTATION_TIMEOUT_MS` (also 10s) — capping it here would abort the scoring
 * at the moment the screen admits it is slow. Do not "fix" the inconsistency.
 */
export function coldStart(signal?: AbortSignal): Promise<ColdStartResponse> {
  return httpClient.post<ColdStartResponse>('/matches/cold-start', undefined, signal)
}

export function getMatches(signal?: AbortSignal): Promise<MatchesResponse> {
  return httpClient.get<MatchesResponse>('/matches', signal)
}

export function setInterest(
  jobId: string,
  interest: Interest,
  signal?: AbortSignal,
): Promise<void> {
  return httpClient.post<void>(
    `/matches/${jobId}/interest`,
    { interest },
    withMutationTimeout(signal),
  )
}

export function getSavedMatches(signal?: AbortSignal): Promise<Match[]> {
  return httpClient.get<Match[]>('/matches/saved', signal)
}

/**
 * Records the candidate's own funnel state for this job (PIVOT-112).
 *
 * 404s when there is no score row for `jobId` — unlike `setInterest`, which
 * is a best-effort taste signal, this is the candidate reporting a fact
 * about their own life, so a failed write must surface as an error rather
 * than a silent no-op.
 */
export function setApplicationStatus(
  jobId: string,
  status: ApplicationStatus,
  signal?: AbortSignal,
): Promise<void> {
  return httpClient.post<void>(
    `/matches/${jobId}/application`,
    { status },
    withMutationTimeout(signal),
  )
}

/**
 * Answers the "did you hear back?" nudge — `POST /api/matches/{job_id}/outcome`.
 *
 * `heard_back: true` lands the job on `'responded'`. `heard_back: false`
 * does not change `application_status`; it snoozes the nudge for another
 * `outcome_prompt_days` server-side (`outcome_due` on the next read reflects
 * it), so the client only ever renders the boolean it gets back.
 */
export function reportOutcome(
  jobId: string,
  heardBack: boolean,
  signal?: AbortSignal,
): Promise<void> {
  return httpClient.post<void>(
    `/matches/${jobId}/outcome`,
    { heard_back: heardBack },
    withMutationTimeout(signal),
  )
}

/**
 * "This job no longer exists" (PIVOT-166) — a signal, never a delete: the
 * server schedules an immediate re-check and only delists on a confirmed
 * result. Always 204s (idempotent — a second report is a no-op) unless
 * `jobId` names no real job at all, so the client never learns whether this
 * particular report confirmed anything; the slate reflects it on its next
 * fetch, not mid-request.
 */
export function reportGone(jobId: string, signal?: AbortSignal): Promise<void> {
  return httpClient.post<void>(
    `/matches/${jobId}/report-gone`,
    undefined,
    withMutationTimeout(signal),
  )
}

/**
 * Records a would-apply (yes/no) answer, or a dismissal, on one matcher-v2
 * card — `POST /api/matches/{job_id}/shelf-answer` (backend PIVOT-298,
 * `InteractionAction`'s `'interested'`/`'dismissed'`/`'applied'`/`'shelved'`).
 * This is the "preference" question: it is the one answer
 * `FeedbackOrderRule` reads (plan §1.9), never the considered-before check
 * `answerCheck` below records.
 *
 * `body.fit_assessment_id` is the exact evidence id `MatcherV2Card` last
 * displayed. A stale id throws `ApiError` with `.status === 409` — the
 * caller re-fetches `/matches/decisions` rather than retrying blind (same
 * contract as `answerCheck`).
 */
export function answerShelfQuestion(
  jobId: string,
  body: ShelfAnswer,
  signal?: AbortSignal,
): Promise<ShelfAnswerOut> {
  return httpClient.post<ShelfAnswerOut>(
    `/matches/${jobId}/shelf-answer`,
    body,
    withMutationTimeout(signal),
  )
}

/**
 * Answers one unverified-fact check with yes/no/unsure (PIVOT-301 Interface:
 * "Expose POST /api/matches/{job_id}/check with yes, no, and unsure.") —
 * the "discovery" (considered-before) question, stored separately from
 * `answerShelfQuestion`'s preference answer and kept out of rank.
 *
 * `fitAssessmentId` is the exact evidence id the card displayed the
 * question against. A stale id (the evidence moved on since the card was
 * fetched, or the job went dark) throws `ApiError` with `.status === 409` —
 * the caller re-fetches the decisions list rather than retrying the same
 * answer blind.
 */
export function answerCheck(
  jobId: string,
  answer: CheckAnswer,
  signal?: AbortSignal,
): Promise<ShelfAnswerOut> {
  return httpClient.post<ShelfAnswerOut>(
    `/matches/${jobId}/check`,
    answer,
    withMutationTimeout(signal),
  )
}

/**
 * Answers one open check-shelf question about what this candidate can do —
 * `POST /api/matches/{jobId}/missing-fact` (backend PIVOT-371).
 *
 * The third question on a card, and deliberately its own call: this one
 * writes candidate EVIDENCE, where `answerShelfQuestion` records a
 * preference and `answerCheck` records novelty. Three contracts, so a stored
 * novelty answer can never be read back as a skill fact.
 *
 * `body` carries both identities the question was displayed with —
 * `fit_assessment_id` from the card's own facts and `candidate_evidence_sha`
 * from the decisions response. A stale either way throws `ApiError` with
 * `.status === 409`, the same contract `answerShelfQuestion` already has:
 * the caller re-fetches `/matches/decisions` rather than retrying blind.
 *
 * A retry of the exact same answer is safe: the response carries
 * `duplicate: true` and the server writes nothing and buys nothing.
 */
export function answerMissingFact(
  jobId: string,
  body: MissingFactAnswer,
  signal?: AbortSignal,
): Promise<MissingFactAnswerOut> {
  return httpClient.post<MissingFactAnswerOut>(
    `/matches/${jobId}/missing-fact`,
    body,
    withMutationTimeout(signal),
  )
}

/**
 * The matcher-v2 dashboard read — `GET /api/matches/decisions` (PIVOT-301,
 * carried forward from PIVOT-298's own documented gap): today's
 * recommendation order, check shelf, and each displayed job's proved fit
 * facts, assembled from persisted evidence alone. Read by `Dashboard.tsx`
 * through `useApiResource`.
 */
export function getMatcherV2Decisions(signal?: AbortSignal): Promise<MatcherV2Decisions> {
  return httpClient.get<MatcherV2Decisions>('/matches/decisions', signal)
}
